import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, BadgePercent, CalendarDays, Clock, Dumbbell, Info, Repeat, Send, Trophy, Users, X,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { ageGroupIndex } from '../../lib/ageGroups';
import { BEST_ENDEAVOURS_NOTE } from '../../lib/copy';
import { fmtFormat } from '../../lib/formats';
import { useSheetDrag } from '../../lib/useSheetDrag';
import {
  fetchQuote, formatPrice, formatPriceOrFree, type MyTeam, type TeamCoachOption,
} from './bookingApi';
import { bookingDurations } from './durations';
import './booking.css';

export interface SlotSelection {
  pitchId: number;
  pitchName: string;
  format: string;
  startUtc: string;
  endUtc: string;
}

const MATCH_TYPES = ['Friendly', 'League', 'Cup'] as const;

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });

/** Coach bookings are whole hours, so every chip reads "1 hour" / "2 hours". */
function fmtDuration(minutes: number): string {
  const h = minutes / 60;
  return h === 1 ? '1 hour' : `${h} hours`;
}

/**
 * Bottom sheet for confirming a slot: duration chips, training/match cards
 * (match adds friendly/league/cup), best-endeavours comments/preferences, then pay or request.
 */
export function SlotSheet({
  selection,
  minMinutes = 60,
  maxMinutes = 180,
  matchRequestsEnabled = true,
  maxBlockWeeks = 1,
  canFoc = false,
  teams,
  coachesByTeam,
  busy,
  onConfirm,
  onClose,
}: {
  selection: SlotSelection;
  /** Cosmetic mirrors of the Booking_* duration policy — the server enforces the real bounds. */
  minMinutes?: number;
  maxMinutes?: number;
  /** Cosmetic mirror of Feature_MatchRequests — the server refuses match creation when off. */
  matchRequestsEnabled?: boolean;
  /** Cosmetic mirror of Booking_MaxBlockWeeks — the server enforces the real cap. <2 hides the chips. */
  maxBlockWeeks?: number;
  /** Admin only: offers the free-of-charge toggle — the server 403s a non-admin sending it. */
  canFoc?: boolean;
  /**
   * Admin only: every active team, and the sheet asks who the booking is for (no default —
   * teams often share a name across age groups, so a preselected first team is a booking for
   * the wrong side waiting to happen). Coaches pick their team on the page instead.
   */
  teams?: MyTeam[];
  /**
   * Admin only: active coaches per team — the payment-request recipient options when booking
   * on behalf of a team the admin doesn't coach.
   */
  coachesByTeam?: Record<number, TeamCoachOption[]>;
  busy: boolean;
  onConfirm: (
    sel: SlotSelection,
    teamId: number | null,
    payerUserId: number | null,
    type: 'Training' | 'Match',
    matchType: string | null,
    notes: string,
    weeks: number,
    focReason: string | null,
  ) => void;
  onClose: () => void;
}) {
  // Teams book pitch time by the hour — 1, 2 or 3 hours with the club's default bounds — for
  // matches as well as training. Mirrors the server's whole-hours rule; odd game lengths are an
  // admin's to place on the scheduler board. 11v11 drops the 1-hour chip: see durations.ts.
  const { minutes: durations, formatFloorMinutes } = useMemo(
    () => bookingDurations(selection.format, minMinutes, maxMinutes),
    [selection.format, minMinutes, maxMinutes],
  );

  const [durationMinutes, setDurationMinutes] = useState(durations[0]);
  const [type, setType] = useState<'Training' | 'Match'>('Training');
  // Deliberately no default: the coach must say what kind of game it is before requesting.
  const [matchType, setMatchType] = useState<(typeof MATCH_TYPES)[number] | null>(null);
  // Same rule for the admin's team picker — '' until they say who the booking is for.
  const [teamId, setTeamId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [weeks, setWeeks] = useState(1);
  // Admin free-of-charge grant — repeats weekly like any other training booking, and the
  // reason is required (the server refuses a FOC booking without one).
  const [foc, setFoc] = useState(false);
  const [focReason, setFocReason] = useState('');
  const sheetRef = useSheetDrag(onClose);

  // Admin mode facts. "On behalf" = a team the admin doesn't coach: the sheet talks to an
  // admin (bookings approve instantly) and the money goes the other way — a payment request
  // to the team's coach instead of the admin's own checkout. The admin's OWN teams keep the
  // ordinary coach flow — except in the past: a slot that has already happened can only be
  // recorded as billed-to-a-coach or free of charge (there is no checkout for history).
  const adminUser = teams !== undefined;
  const isPast = Date.parse(selection.startUtc) <= Date.now();
  const team = teams?.find((t) => t.id === teamId) ?? null;
  const onBehalf = team !== null && !team.isCoached;
  // An admin's MATCH also bills a coach (matches auto-approve, so a priced one must name who
  // owes it — the server refuses an unbilled non-FOC admin match), as does anything in the past.
  const billsCoach = onBehalf || (adminUser && (isPast || type === 'Match'));
  const coaches = useMemo<TeamCoachOption[]>(
    () => (team && (isPast || type === 'Match' || !team.isCoached) ? coachesByTeam?.[team.id] ?? [] : []),
    [team, isPast, type, coachesByTeam],
  );
  const [payerUserId, setPayerUserId] = useState<number | null>(null);
  // Default to the first active coach; the list can resolve after the sheet mounts, and a
  // team change swaps it wholesale (PlaceSheet's payer-picker pattern).
  useEffect(() => {
    setPayerUserId(coaches[0]?.userId ?? null);
  }, [coaches]);
  // A payment request needs a recipient — for anything the coach will owe money on.
  const payerNeeded = billsCoach && !foc;

  // A fresh slot (or a policy change that reshapes the chips) starts at the shortest booking,
  // for matches as well as training.
  useEffect(() => {
    setDurationMinutes(durations[0]);
  }, [selection, durations]);

  useEffect(() => {
    setWeeks(1);
    setFoc(false);
    setFocReason('');
    setTeamId('');
  }, [selection]);

  // If the features query resolves to "off" while the sheet is open, drop a stale Match choice.
  useEffect(() => {
    if (!matchRequestsEnabled) setType('Training');
  }, [matchRequestsEnabled]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const endUtc = useMemo(() => {
    const end = new Date(selection.startUtc);
    end.setMinutes(end.getMinutes() + durationMinutes);
    return end.toISOString();
  }, [selection.startUtc, durationMinutes]);

  // Live price for the chosen duration — the same server-side pricing resolution the create
  // path runs, so the number here is the number at checkout. Matches are requested (fee may
  // be waived on approval), so the quote is training-only.
  const quote = useQuery({
    queryKey: ['booking-quote', selection.pitchId, selection.startUtc, endUtc],
    queryFn: () => fetchQuote(selection.pitchId, selection.startUtc, endUtc),
    // FOC has nothing to pay whatever the price rule says (and needs no rule to exist), so the
    // quote would only put a number on screen that the booking then ignores.
    enabled: type === 'Training' && !foc,
    staleTime: 60_000,
  });
  const pricePence = type === 'Training' && !foc ? (quote.data?.pricePence ?? null) : null;

  const dayLabel = new Date(selection.startUtc).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/London',
  });

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={sheetRef}
        className="sheet bk-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`Book ${selection.pitchName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bk-sheet-handle" aria-hidden />
        <button type="button" className="bk-sheet-close" aria-label="Close" onClick={onClose}>
          <X size={18} aria-hidden />
        </button>

        <header className="bk-sheet-header">
          <div className="bk-sheet-title-row">
            <strong className="bk-sheet-title">{selection.pitchName}</strong>
            <span className="pitch-format-badge">{fmtFormat(selection.format)}</span>
          </div>
          <div className="bk-sheet-when">
            <span><CalendarDays size={14} aria-hidden /> {dayLabel}</span>
            <span><Clock size={14} aria-hidden /> {fmtTime(selection.startUtc)}–{fmtTime(endUtc)}</span>
          </div>
        </header>

        {teams ? (
          <div className="bk-section">
            <label className="bk-section-label" htmlFor="bk-team">
              <Users size={13} aria-hidden /> Booking for{' '}
              {teamId === '' ? <em className="bk-required">— choose a team</em> : null}
            </label>
            <select
              id="bk-team"
              className="ui-select"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="" disabled>
                Choose a team…
              </option>
              {/* Youngest first, Open last — an admin scans the club's ladder, not the DB order. */}
              {[...teams]
                .sort(
                  (a, b) =>
                    ageGroupIndex(a.ageGroup) - ageGroupIndex(b.ageGroup) ||
                    a.name.localeCompare(b.name),
                )
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.ageGroup} {t.name} ({fmtFormat(t.defaultFormat)})
                  </option>
                ))}
            </select>
          </div>
        ) : null}

        {payerNeeded ? (
          <div className="bk-section">
            <label className="bk-section-label" htmlFor="bk-payer">
              <Send size={13} aria-hidden /> Send payment request to
            </label>
            {coaches.length === 0 ? (
              <span className="bk-repeat-note">
                <AlertTriangle size={13} aria-hidden />
                This team has no active coach to bill — book it free of charge instead.
              </span>
            ) : (
              <select
                id="bk-payer"
                className="ui-select"
                value={payerUserId ?? ''}
                onChange={(e) => setPayerUserId(Number(e.target.value))}
              >
                {coaches.map((c) => (
                  <option key={c.userId} value={c.userId}>{c.displayName}</option>
                ))}
              </select>
            )}
          </div>
        ) : null}

        <div className="bk-section">
          <span className="bk-section-label" id="bk-duration-label">Duration</span>
          <div className="bk-chips" role="radiogroup" aria-labelledby="bk-duration-label">
            {durations.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={durationMinutes === m}
                className={`bk-chip ${durationMinutes === m ? 'bk-chip-active' : ''}`}
                onClick={() => setDurationMinutes(m)}
              >
                {fmtDuration(m)}
              </button>
            ))}
          </div>
          {formatFloorMinutes !== null ? (
            <span className="bk-repeat-note">
              <Clock size={13} aria-hidden />
              {fmtFormat(selection.format)} is booked in blocks of{' '}
              {fmtDuration(formatFloorMinutes)} — a game does not fit inside one hour.
            </span>
          ) : null}
        </div>

        {matchRequestsEnabled ? (
          <div className="bk-section">
            <span className="bk-section-label" id="bk-type-label">Booking type</span>
            <div className="type-cards" role="radiogroup" aria-labelledby="bk-type-label">
              <button
                type="button"
                role="radio"
                aria-checked={type === 'Training'}
                className={`type-card ${type === 'Training' ? 'type-card-active' : ''}`}
                onClick={() => setType('Training')}
              >
                <Dumbbell size={17} aria-hidden />
                <span className="type-card-title">Training</span>
                <span className="type-card-sub">
                  {billsCoach ? 'Booked instantly — the coach is billed' : 'Pay online — confirmed instantly'}
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={type === 'Match'}
                className={`type-card ${type === 'Match' ? 'type-card-active' : ''}`}
                onClick={() => setType('Match')}
              >
                <Trophy size={17} aria-hidden />
                <span className="type-card-title">Match</span>
                <span className="type-card-sub">
                  {adminUser ? 'Booked & approved instantly' : 'Requested — the club approves it'}
                </span>
              </button>
            </div>
          </div>
        ) : null}

        {canFoc ? (
          <div className="bk-section">
            <label className="bk-foc-toggle">
              <input
                type="checkbox"
                checked={foc}
                onChange={(e) => setFoc(e.target.checked)}
              />
              <BadgePercent size={15} aria-hidden />
              Free of charge
            </label>
            {foc ? (
              <>
                <label className="bk-section-label bk-foc-reason-label" htmlFor="bk-foc-reason">
                  Why is this free of charge?{' '}
                  {focReason.trim() === '' ? <em className="bk-required">— required</em> : null}
                </label>
                <textarea
                  id="bk-foc-reason"
                  className="sheet-notes"
                  placeholder="e.g. Goodwill gesture agreed with the committee"
                  maxLength={500}
                  value={focReason}
                  onChange={(e) => setFocReason(e.target.value)}
                />
              </>
            ) : null}
          </div>
        ) : null}

        {matchRequestsEnabled && type === 'Match' ? (
          <div className="bk-important" role="note">
            <AlertTriangle size={16} aria-hidden />
            {adminUser ? (
              <p>
                <strong>Standard matchdays belong on the scheduler board</strong> — only book a
                match here for games outside the norm: rearranged fixtures, extra friendlies,
                cup replays and the like.
              </p>
            ) : (
              <p>
                <strong>Standard matchdays are handled separately</strong> — the club allocates
                those pitches for you. Only request a match slot here for games outside the norm:
                rearranged fixtures, extra friendlies, cup replays and the like.
              </p>
            )}
          </div>
        ) : null}

        {matchRequestsEnabled && type === 'Match' ? (
          <div className="bk-section">
            <span className="bk-section-label" id="bk-match-label">
              Match type {matchType === null ? <em className="bk-required">— choose one</em> : null}
            </span>
            <div className="bk-chips" role="radiogroup" aria-labelledby="bk-match-label">
              {MATCH_TYPES.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={matchType === m}
                  className={`bk-chip ${matchType === m ? 'bk-chip-active' : ''}`}
                  onClick={() => setMatchType(m)}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {type === 'Training' && maxBlockWeeks >= 2 && !isPast ? (
          <div className="bk-section">
            <span className="bk-section-label" id="bk-repeat-label">Repeat weekly</span>
            <div className="bk-chips" role="radiogroup" aria-labelledby="bk-repeat-label">
              <button
                type="button"
                role="radio"
                aria-checked={weeks === 1}
                className={`bk-chip ${weeks === 1 ? 'bk-chip-active' : ''}`}
                onClick={() => setWeeks(1)}
              >
                Just once
              </button>
              {Array.from({ length: maxBlockWeeks - 1 }, (_, i) => i + 2).map((w) => (
                <button
                  key={w}
                  type="button"
                  role="radio"
                  aria-checked={weeks === w}
                  className={`bk-chip ${weeks === w ? 'bk-chip-active' : ''}`}
                  onClick={() => setWeeks(w)}
                >
                  {w} weeks
                </button>
              ))}
            </div>
            {weeks > 1 ? (
              <span className="bk-repeat-note">
                <Repeat size={13} aria-hidden />
                {foc
                  ? 'Same slot each week, free of charge — you’ll review every week before booking.'
                  : onBehalf
                    ? 'Same slot each week — you’ll review every week; the coach is billed per week.'
                    : 'Same slot each week — you’ll review every week and the total before paying.'}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="bk-section">
          <label className="bk-section-label" htmlFor="bk-notes">
            {type === 'Training' ? 'Comments' : 'Comments / preferences'}
          </label>
          <textarea
            id="bk-notes"
            className="sheet-notes"
            placeholder={
              type === 'Training'
                ? 'e.g. Anything the club should know about this session'
                : 'e.g. Ideally need a later kick off'
            }
            maxLength={1000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="bk-hints">
          <Info size={15} aria-hidden />
          <div>
            {/* The best-endeavours line is club-to-coach wording — noise on an admin booking. */}
            {!billsCoach ? <p>{BEST_ENDEAVOURS_NOTE}</p> : null}
            {isPast ? (
              <p>
                This slot is in the past — you&apos;re recording that it was used
                {foc ? ', free of charge.' : '; the coach is billed and the club chases payment.'}
              </p>
            ) : null}
            {foc ? (
              <p>Free of charge — the booking confirms immediately with nothing to pay.</p>
            ) : billsCoach ? (
              !isPast ? (
                <p>
                  The slot is booked for the team straight away — a payment request is emailed to
                  the coach, and the club chases payment. Nothing for you to pay now.
                </p>
              ) : null
            ) : type === 'Match' ? (
              <p>Match slots are requested — an admin approves (and may waive the fee) before it's confirmed.</p>
            ) : (
              <p>The slot is held for a short time while you pay.</p>
            )}
          </div>
        </div>

        {type === 'Training' ? (
          <div className="bk-price-row">
            <span className="bk-price-label">
              {weeks > 1 ? 'Price per week' : 'Price'} · {fmtDuration(durationMinutes)}
            </span>
            <strong className="bk-price-value">
              {foc
                ? 'Free of charge'
                : quote.isLoading
                  ? '…'
                  : pricePence !== null
                    ? formatPriceOrFree(pricePence)
                    : 'Not set — contact the club'}
            </strong>
          </div>
        ) : null}

        <div className="sheet-actions">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Back
          </Button>
          <Button
            loading={busy}
            disabled={
              (adminUser && teamId === '')
              || (payerNeeded && payerUserId === null)
              || (type === 'Match' && matchType === null)
              || (foc && focReason.trim() === '')
            }
            onClick={() =>
              onConfirm(
                { ...selection, endUtc },
                adminUser && teamId !== '' ? teamId : null,
                // A £0 quote has nothing to bill — the server refuses a zero-price payer, and
                // the slot confirms free of charge without one.
                payerNeeded && pricePence !== 0 ? payerUserId : null,
                type,
                type === 'Match' ? matchType : null,
                notes,
                type === 'Training' ? weeks : 1,
                foc ? focReason.trim() : null,
              )
            }
          >
            {type === 'Match'
              ? adminUser ? 'Book match' : 'Request slot'
              : weeks > 1
                ? `Review ${weeks} weeks`
                : foc || pricePence === 0
                  ? 'Book free of charge'
                  : billsCoach
                    ? 'Book & send payment request'
                    : pricePence !== null
                      ? `Book & pay ${formatPrice(pricePence)}`
                      : 'Book & pay'}
          </Button>
        </div>
      </div>
    </div>
  );
}
